// Module-level singleton pointing at the currently mounted <video> element.
//
// The VideoPlayer sets this on mount and clears it on unmount. Hooks that
// need imperative access to the video (useVideoDucking, future preview
// scheduling) read it through `getActiveVideo()`. This avoids threading a
// ref through the component tree when there is only ever one <video> at a
// time in the TTS/STS views.
//
// React parity: the legacy code used `document.getElementById('videoElement')`
// for the same purpose. A module-level ref is the React equivalent — a
// singleton, no DOM query, no tree wiring.

let activeVideo: HTMLVideoElement | null = null;
const changeListeners = new Set<() => void>();

export function setActiveVideo(el: HTMLVideoElement | null): void {
  if (activeVideo === el) return;
  activeVideo = el;
  // Notify listeners so e.g. useVideoDucking can push the current mixer
  // values onto the freshly-mounted <video> element. Without this, a new
  // VideoPlayer mounts at the browser-default volume (1.0) and stays
  // there until the user touches the fader (#168). Errors in listeners
  // must not break the registration — swallow + log.
  for (const cb of changeListeners) {
    try { cb(); } catch (e) { console.warn('[activeVideo] listener failed', e); }
  }
}

export function getActiveVideo(): HTMLVideoElement | null {
  return activeVideo;
}

/**
 * Clear the singleton — but only if it still points at `el`. Use this for
 * every teardown path instead of `setActiveVideo(null)`.
 *
 * Why: when FreeChoiceView swaps `<TTSView/>` for `<STSView/>` (custom
 * asset, TTS↔STS switch) both views own a VideoPlayer and React replaces
 * one with the other in a single commit. Order of that commit:
 *   mutation phase → old <video> ref callback fires with null
 *   layout phase   → new <video> ref callback fires with the new element
 *   passive phase  → the OLD VideoPlayer's useEffect cleanup runs last
 * An unconditional `setActiveVideo(null)` in that cleanup wiped the freshly
 * registered element, so the STS master preview (and TTS after the reverse
 * switch) found no video to play — while the recording overlay, which owns
 * its own <video>, kept working. Reported 2026-09-15.
 */
export function releaseActiveVideo(el: HTMLVideoElement | null): void {
  if (el === null || activeVideo !== el) return;
  setActiveVideo(null);
}

/** Subscribe to active-video changes. Caller receives no payload — read
 *  the current element via `getActiveVideo()`. Returns an unsubscribe
 *  function. Module-level subscribers (e.g. useVideoDucking) intentionally
 *  never unsubscribe since they live for the app's lifetime. */
export function onActiveVideoChange(cb: () => void): () => void {
  changeListeners.add(cb);
  return () => {
    changeListeners.delete(cb);
  };
}

/**
 * Drag-time scrub helper. Fire-and-forget seek to the active <video>
 * element with guards that keep streaming sources (WebDAV) from drowning
 * under one Range request per pointermove.
 *
 * Guards:
 *   - `readyState >= 2` (HAVE_CURRENT_DATA): wait for the first frame to
 *     be decoded so we don't seek-and-reset on a cold-loaded element.
 *   - `!v.seeking`: skip while a previous seek is still in flight. Without
 *     this, holding-and-jiggling the playhead piles up Range requests on
 *     the proxy → element gets stuck in `seeking=true` forever and play()
 *     stops working entirely on streamed videos.
 *   - 0.05s no-op skip: cheap pre-filter for redundant assignments.
 *
 * Caller throttles via rAF (see `scheduleVideoScrub`); this function is
 * the unguarded "do it now" sink the rAF callback dispatches to.
 */
export function scrubActiveVideo(sec: number): void {
  const v = activeVideo;
  if (!v || !Number.isFinite(v.duration)) return;
  if (v.readyState < 2) return;
  if (v.seeking) return;
  const target = Math.max(0, Math.min(sec, v.duration || 0));
  if (Math.abs(v.currentTime - target) < 0.05) return;
  try {
    v.currentTime = target;
  } catch {
    /* ignore */
  }
}

// rAF-throttled scrub. pointermove fires at the input device's rate
// (often 240+ Hz on trackpads); rAF coalesces to ~60Hz and combined with
// the `!seeking` guard above caps the in-flight Range requests to 1.
let pendingScrubSec: number | null = null;
let scrubRafHandle: number | null = null;

export function scheduleVideoScrub(sec: number): void {
  pendingScrubSec = sec;
  if (scrubRafHandle !== null) return;
  scrubRafHandle = requestAnimationFrame(() => {
    scrubRafHandle = null;
    const target = pendingScrubSec;
    pendingScrubSec = null;
    if (target === null) return;
    scrubActiveVideo(target);
  });
}

/**
 * Seek the <video> element to `t` and resolve only after the seek has
 * actually landed (i.e. `seeking` flipped back to false / `seeked` fired).
 *
 * Why this exists: HTMLMediaElement seeks are asynchronous. Setting
 * `v.currentTime = 0` returns immediately, but if you call `v.play()`
 * before the seek completes, the element starts decoding from the OLD
 * playhead for a few ms before snapping to the new one — and because the
 * preview pipeline does NOT mute the <video>, that's audible as a snippet
 * of the old position's native audio track.
 *
 * Repro path: user clicks video to stop mid-play (currentTime stays at the
 * stopped position), clicks again to restart. play(0) sets currentTime=0
 * and fires v.play() too early → tail snippet from the previous playhead.
 *
 * The fallback timeout (200ms) protects against browsers that swallow the
 * seeked event when currentTime is already at the requested value, or when
 * the element is in an oddball readyState. 200ms is well over the typical
 * seeked latency (1–2 frames) and well under any user-perceivable delay
 * before audio starts.
 */
export function seekVideoTo(
  v: HTMLVideoElement,
  t: number,
  timeoutMs = 200,
): Promise<void> {
  return new Promise<void>((resolve) => {
    const target = Math.max(0, Math.min(t, Number.isFinite(v.duration) ? v.duration : t));
    // Fast path: already there, no seek will fire.
    if (Math.abs(v.currentTime - target) < 0.001 && !v.seeking) {
      try { v.currentTime = target; } catch { /* ignore */ }
      resolve();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      v.removeEventListener('seeked', finish);
      window.clearTimeout(handle);
      resolve();
    };
    v.addEventListener('seeked', finish, { once: true });
    const handle = window.setTimeout(finish, timeoutMs);
    try { v.currentTime = target; } catch { finish(); }
  });
}
